import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Grid,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  LinearProgress,
  Tooltip,
  Divider,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  LocalOffer as PriceIcon,
} from '@mui/icons-material';
import {
  usePrices,
  useCreatePrice,
  useUpdatePrice,
  useDeletePrice,
  useTogglePriceStatus,
} from '../hooks/usePricing';
import { Price } from '../types';

const EGGS_PER_CRATE = 30;

const formatDateRange = (from?: string, to?: string): string => {
  if (!from && !to) return '—';
  if (from && !to) return `From ${from}`;
  if (!from && to) return `Until ${to}`;
  return `${from} → ${to}`;
};

const formatVolumeTier = (min?: number, max?: number): string => {
  if (!min && !max) return 'Any';
  if (min && !max) return `${min}+ crates`;
  if (!min && max) return `Up to ${max}`;
  return `${min} – ${max}`;
};

const extractNumeric = (value: string | number): string => {
  if (typeof value === 'number') return value.toString();
  return value.replace(/[^0-9.]/g, '');
};

const EMPTY_FORM = {
  name: '',
  price_per_crate: '',
  price_per_egg: '',
  markup_percentage: '',
  effective_from: '',
  effective_to: '',
  is_active: true,
  min_crates: '',
  max_crates: '',
  notes: '',
};

const PricesPage: React.FC = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPrice, setEditingPrice] = useState<Price | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Price | null>(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning';
  }>({ open: false, message: '', severity: 'success' });

  const {
    data: pricesData,
    isLoading,
    isFetching,
  } = usePrices({
    page: paginationModel.page + 1,
    per_page: paginationModel.pageSize,
  });
  const createMutation = useCreatePrice();
  const updateMutation = useUpdatePrice();
  const deleteMutation = useDeletePrice();
  const toggleMutation = useTogglePriceStatus();

  const prices = pricesData?.data || [];
  const totalCount = pricesData?.meta?.total || 0;

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (price?: Price) => {
    if (price) {
      setEditingPrice(price);
      setFormData({
        name: price.name || '',
        price_per_crate: extractNumeric(price.price_per_crate),
        price_per_egg: extractNumeric(price.price_per_egg),
        markup_percentage: price.markup_percentage?.toString() || '',
        effective_from: price.effective_from || '',
        effective_to: price.effective_to || '',
        is_active: price.is_active,
        min_crates: price.min_crates?.toString() || '',
        max_crates: price.max_crates?.toString() || '',
        notes: price.notes || '',
      });
    } else {
      setEditingPrice(null);
      setFormData(EMPTY_FORM);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPrice(null);
  };

  const handlePricePerCrateChange = (value: string) => {
    const numeric = parseFloat(value);
    const derived = !isNaN(numeric) && numeric > 0 ? (numeric / EGGS_PER_CRATE).toFixed(4) : '';
    setFormData((f) => ({ ...f, price_per_crate: value, price_per_egg: derived }));
  };

  const handleSubmit = async () => {
    if (!formData.price_per_crate || parseFloat(formData.price_per_crate) <= 0) {
      showSnackbar('Please enter a valid price per crate', 'warning');
      return;
    }
    if (!formData.price_per_egg || parseFloat(formData.price_per_egg) <= 0) {
      showSnackbar('Please enter a valid price per egg', 'warning');
      return;
    }
    if (
      formData.effective_from &&
      formData.effective_to &&
      formData.effective_to < formData.effective_from
    ) {
      showSnackbar('"Effective To" must be on or after "Effective From"', 'warning');
      return;
    }

    try {
      const data = {
        name: formData.name || undefined,
        price_per_crate: parseFloat(formData.price_per_crate),
        price_per_egg: parseFloat(formData.price_per_egg),
        markup_percentage: formData.markup_percentage
          ? parseFloat(formData.markup_percentage)
          : undefined,
        effective_from: formData.effective_from || undefined,
        effective_to: formData.effective_to || undefined,
        is_active: formData.is_active,
        min_crates: formData.min_crates ? parseInt(formData.min_crates) : undefined,
        max_crates: formData.max_crates ? parseInt(formData.max_crates) : undefined,
        notes: formData.notes || undefined,
      };

      if (editingPrice) {
        await updateMutation.mutateAsync({ id: editingPrice.id, data });
        showSnackbar('Price updated successfully');
      } else {
        await createMutation.mutateAsync(data);
        showSnackbar('Price created successfully');
      }
      handleCloseDialog();
    } catch {
      showSnackbar('Failed to save price', 'error');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      showSnackbar('Price deleted');
    } catch {
      showSnackbar('Failed to delete price', 'error');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleToggleStatus = async (id: number) => {
    try {
      await toggleMutation.mutateAsync(id);
    } catch {
      showSnackbar('Failed to update status', 'error');
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2,
      minWidth: 140,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" height="100%">
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.87)' }}>
            {params.value || <em style={{ opacity: 0.45 }}>Unnamed</em>}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'price_per_crate',
      headerName: 'Per Crate',
      flex: 1,
      minWidth: 120,
      headerAlign: 'right',
      align: 'right',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" justifyContent="flex-end" height="100%">
          <Typography variant="body2" fontWeight={700} sx={{ color: '#66bb6a' }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'price_per_egg',
      headerName: 'Per Egg',
      flex: 0.9,
      minWidth: 110,
      headerAlign: 'right',
      align: 'right',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" justifyContent="flex-end" height="100%">
          <Typography variant="body2" fontWeight={600} sx={{ color: '#81c784' }}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      flex: 0.7,
      minWidth: 90,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" justifyContent="center" height="100%">
          <Chip
            label={params.value ? 'Active' : 'Inactive'}
            color={params.value ? 'success' : 'default'}
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      flex: 0.8,
      minWidth: 110,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} height="100%">
          <Tooltip title={params.row.is_active ? 'Deactivate' : 'Activate'}>
            <IconButton
              size="small"
              onClick={() => handleToggleStatus(params.row.id)}
              sx={{ color: params.row.is_active ? '#f57c00' : '#66bb6a' }}
            >
              {params.row.is_active ? (
                <ToggleOffIcon fontSize="small" />
              ) : (
                <ToggleOnIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(params.row)}
              sx={{ color: '#2196f3' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => setDeleteTarget(params.row)}
              sx={{ color: '#f44336' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {isFetching && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#ff9800,#ffa726)' },
          }}
        />
      )}

      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={1.5}>
          <PriceIcon sx={{ color: '#ff9800', fontSize: 28 }} />
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#fff', lineHeight: 1.2 }}>
              Prices
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)' }}>
              {totalCount} price tier{totalCount !== 1 ? 's' : ''}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            background: 'linear-gradient(135deg,#ff9800,#ffa726)',
            '&:hover': { background: 'linear-gradient(135deg,#f57c00,#ff9800)' },
            fontWeight: 600,
          }}
        >
          Add Price
        </Button>
      </Box>

      {/* Grid */}
      <Paper
        sx={{ flex: 1, height: 'calc(100vh - 180px)', border: '1px solid rgba(255,152,0,0.3)' }}
      >
        <DataGrid
          rows={prices}
          columns={columns}
          loading={isLoading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          rowCount={totalCount}
          disableRowSelectionOnClick
          sx={{
            border: 'none',
            height: '100%',
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(255,152,0,0.15)',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: 'rgba(255,152,0,0.12)',
              borderBottom: '1px solid rgba(255,152,0,0.3)',
            },
            '& .MuiDataGrid-columnHeaderTitle': { color: '#fff', fontWeight: 600 },
            '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(255,152,0,0.06)' },
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid rgba(255,152,0,0.3)',
              bgcolor: 'rgba(255,152,0,0.05)',
            },
          }}
        />
      </Paper>

      {/* Add / Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { border: '1px solid rgba(255,152,0,0.3)' } } }}
      >
        <DialogTitle
          sx={{ borderBottom: '1px solid rgba(255,152,0,0.2)', color: '#ff9800', pb: 1.5 }}
        >
          {editingPrice ? 'Edit Price' : 'New Price Tier'}
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2.5 }}>
            <TextField
              label="Name (optional)"
              value={formData.name}
              onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
              fullWidth
              placeholder="e.g. Retail, Wholesale, Bulk"
            />

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: -0.5 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', px: 1 }}>
                Pricing
              </Typography>
            </Divider>

            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Price per Crate (GH₵)"
                  type="number"
                  value={formData.price_per_crate}
                  onChange={(e) => handlePricePerCrateChange(e.target.value)}
                  fullWidth
                  required
                  slotProps={{ htmlInput: { step: '0.01', min: '0' } }}
                  helperText="Auto-derives per-egg price"
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Price per Egg (GH₵)"
                  type="number"
                  value={formData.price_per_egg}
                  onChange={(e) => setFormData((f) => ({ ...f, price_per_egg: e.target.value }))}
                  fullWidth
                  required
                  slotProps={{ htmlInput: { step: '0.0001', min: '0' } }}
                  helperText={`÷ ${EGGS_PER_CRATE} eggs per crate`}
                />
              </Grid>
            </Grid>

            <TextField
              label="Markup Percentage (optional)"
              type="number"
              value={formData.markup_percentage}
              onChange={(e) => setFormData((f) => ({ ...f, markup_percentage: e.target.value }))}
              fullWidth
              slotProps={{ htmlInput: { step: '0.01', min: '0', max: '100' } }}
              placeholder="e.g. 10"
            />

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: -0.5 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', px: 1 }}>
                Volume Tier (optional)
              </Typography>
            </Divider>

            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Min Crates"
                  type="number"
                  value={formData.min_crates}
                  onChange={(e) => setFormData((f) => ({ ...f, min_crates: e.target.value }))}
                  fullWidth
                  slotProps={{ htmlInput: { min: '0' } }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Max Crates"
                  type="number"
                  value={formData.max_crates}
                  onChange={(e) => setFormData((f) => ({ ...f, max_crates: e.target.value }))}
                  fullWidth
                  slotProps={{ htmlInput: { min: '0' } }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: -0.5 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)', px: 1 }}>
                Validity (optional)
              </Typography>
            </Divider>

            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Effective From"
                  type="date"
                  value={formData.effective_from}
                  onChange={(e) => setFormData((f) => ({ ...f, effective_from: e.target.value }))}
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label="Effective To"
                  type="date"
                  value={formData.effective_to}
                  onChange={(e) => setFormData((f) => ({ ...f, effective_to: e.target.value }))}
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true } }}
                  error={
                    !!formData.effective_from &&
                    !!formData.effective_to &&
                    formData.effective_to < formData.effective_from
                  }
                  helperText={
                    !!formData.effective_from &&
                    !!formData.effective_to &&
                    formData.effective_to < formData.effective_from
                      ? 'Must be on or after "From" date'
                      : ''
                  }
                />
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData((f) => ({ ...f, is_active: e.target.checked }))}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#ff9800' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#ff9800',
                    },
                  }}
                />
              }
              label="Active"
            />

            <TextField
              label="Notes (optional)"
              value={formData.notes}
              onChange={(e) => setFormData((f) => ({ ...f, notes: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ borderTop: '1px solid rgba(255,152,0,0.2)', px: 3, py: 2, gap: 1 }}>
          <Button onClick={handleCloseDialog} sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSaving}
            sx={{ background: 'linear-gradient(135deg,#ff9800,#ffa726)', fontWeight: 600 }}
          >
            {isSaving ? 'Saving…' : editingPrice ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { border: '1px solid rgba(244,67,54,0.3)' } } }}
      >
        <DialogTitle sx={{ color: '#f44336' }}>Delete Price</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteTarget?.name || 'this price tier'}</strong>? This cannot be
            undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={deleteMutation.isPending}
            sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#d32f2f' }, fontWeight: 600 }}
          >
            {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PricesPage;
